import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { I18nextProvider } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useHydrateAtoms } from 'jotai/utils';
import { queryClientAtom } from 'jotai-tanstack-query';
import { useAccountSummaryV2Query } from '@syf-api-library/account-summary';
import { ModalHeader } from 'syf-component-library/ui/patterns/SlideInModalPortal';
import { useFeatureFlag } from '../../hooks';
import makeAPaymentStore from '../../store';
import { useConversionCount } from '../../store/conversionCountAtom';
import { MakeAPaymentView, useCurrentViewAtom } from '../../store/currentViewAtom';
import { multiPaymentStateAtom, useIsErrorView, useShowCloseInterstitial } from '../../store/multiPaymentAtoms';
import { MAPFlowTypes } from '../../typings/deepLink';
import { DATA_REASON, DATA_TYPE, PAGE_FUNCTION, SUB_FUNCTION } from '../../const/analytics';
import BuildVersion from '../../mapBuildVersion';
import i18n, { t } from '../../strings/i18n';
import DrawerHeader from '../atoms/DrawerHeader';
import { ModalContentWrapper, ScrollTop, SlideInModalPortalWrapper } from '../atoms/styled/SlideInModal';
import { CardError } from '../molecules/ErrorComponents/CardError';
import CoreWrapper from './CoreWrapper';
const HydrateAtoms = ({ children, queryClient }) => {
    useHydrateAtoms([[queryClientAtom, queryClient]]);
    return children;
};
const PaymentModal = ({ accountId, accountLast4, additionalProps, apiErrorCallback, containerId, featureFlags, handleClose: handleBaseClose, handleCloseOnConversion, helpNumber, hosts, id, isOpen, lang, triggerAnalyticsEvent }) => {
    const headerRef = useRef(null);
    const scrollToTopRef = useRef(null);
    const normalizedFeatureFlags = useFeatureFlag(featureFlags);
    const [showCloseConfirmation, setShowCloseConfirmationAtom] = useShowCloseInterstitial();
    const [isErrorView, setIsErrorView] = useIsErrorView();
    const [, setConversionCount] = useConversionCount();
    const [currentViewAtom, setCurrentViewAtom] = useCurrentViewAtom();
    const languageInStorage = localStorage.getItem('language');
    const queryClient = useQueryClient();
    const apiConfig = useMemo(() => ({
        config: {
            apiErrorCallback,
            hosts,
            lang
        },
        keyProps: {
            accountId,
            accountIdType: 'guid'
        },
        queryOptions: Object.assign(Object.assign({}, queryClient.getQueryDefaults()), { enabled: isOpen })
    }), [accountId, apiErrorCallback, hosts, isOpen, lang, queryClient]);
    const { data: accountSummary, isLoading } = useAccountSummaryV2Query(apiConfig);
    const isMultiPaymentFlow = additionalProps.flow.type === MAPFlowTypes.MULTI_PAYMENT_FLOW &&
        featureFlags.isDigitalCollectionsEnabled;
    useEffect(() => {
        if (languageInStorage) {
            i18n.changeLanguage(languageInStorage).catch((error) => error);
        }
    }, [languageInStorage]);
    useEffect(() => {
        if (isOpen) {
            setShowCloseConfirmationAtom(false);
            setConversionCount(0);
            setIsErrorView(false);
        }
    }, [isOpen, setConversionCount, setIsErrorView, setShowCloseConfirmationAtom]);
    useEffect(() => {
        if (scrollToTopRef.current)
            scrollToTopRef.current.scrollIntoView();
    }, [currentViewAtom.view]);
    const handleCloseWithInterstitial = useCallback(() => {
        if (!isMultiPaymentFlow ||
            currentViewAtom.view === MakeAPaymentView.MultiPaymentError ||
            isErrorView) {
            handleBaseClose();
            return;
        }
        if (currentViewAtom.view === MakeAPaymentView.MultiPaymentConfirmation) {
            handleCloseOnConversion === null || handleCloseOnConversion === void 0 ? void 0 : handleCloseOnConversion();
            handleBaseClose();
            // Need to reset multi payment state so that it opens on details flow, we use store not hook directly due to dependency issues
            makeAPaymentStore.set(multiPaymentStateAtom, {});
            setCurrentViewAtom({
                type: 'updateView',
                view: MakeAPaymentView.MultiPaymentDetails
            });
            return;
        }
        if (!showCloseConfirmation) {
            setShowCloseConfirmationAtom(true);
            return;
        }
        triggerAnalyticsEvent({
            pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
            pageName: 'cancel',
            pageSubFunction: SUB_FUNCTION.MAKE_PAYMENT.PAYMENT
        });
        handleBaseClose();
        setShowCloseConfirmationAtom(false);
    }, [
        currentViewAtom.view,
        handleBaseClose,
        handleCloseOnConversion,
        isErrorView,
        isMultiPaymentFlow,
        setShowCloseConfirmationAtom,
        showCloseConfirmation,
        triggerAnalyticsEvent,
        setCurrentViewAtom
    ]);
    const handleMAPClose = () => {
        if (isMultiPaymentFlow) {
            handleCloseWithInterstitial();
        }
        else {
            handleBaseClose();
        }
    };
    return (_jsx(I18nextProvider, Object.assign({ i18n: i18n }, { children: _jsxs(SlideInModalPortalWrapper, Object.assign({ ariaLabel: "Make a Payment", className: "PaymentModal", containerId: containerId, escapeModalHandler: handleMAPClose, header: _jsx(ModalHeader, { closeModal: handleMAPClose, content: t('makePayment.makeAPayment'), dataReason: DATA_REASON.MAKE_A_PAYMENT_HEADER.X_BUTTON_HEADER, dataType: DATA_TYPE.MAKE_A_PAYMENT, headerRef: headerRef, hideClose: showCloseConfirmation }), id: id, isCloseInterstitialShown: showCloseConfirmation, isOpen: isOpen }, { children: [_jsx(ScrollTop, { ref: scrollToTopRef }), _jsx(DrawerHeader, { accountSummary: accountSummary, hosts: hosts, isLoading: isLoading, multiAccountLast4: accountLast4 }), _jsx("input", { id: "MAP_build_version", type: "hidden", value: BuildVersion.mapBuildVersion }), _jsx(ModalContentWrapper, { children: _jsx(ErrorBoundary, Object.assign({ fallback: _jsx(CardError, {}) }, { children: _jsx(HydrateAtoms, Object.assign({ queryClient: queryClient }, { children: _jsx(CoreWrapper, { accountId: accountId, accountLast4: accountLast4, additionalProps: additionalProps, apiConfig: apiConfig, apiErrorCallback: apiErrorCallback, containerId: containerId, featureFlags: normalizedFeatureFlags, handleClose: handleCloseWithInterstitial, handleCloseOnConversion: handleCloseOnConversion, headerRef: headerRef, helpNumber: helpNumber, hosts: hosts, id: id, isOpen: isOpen, lang: lang, triggerAnalyticsEvent: triggerAnalyticsEvent }) })) })) })] })) })));
};
export default PaymentModal;
